'use client';

import { forwardRef, useContext, useId } from 'react';

import { css, cx } from '@/styled-system/css';

import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { ModalRootContext } from './context/modal-root.context';
import { modalHeaderStyles } from './styles/modal-header.styles';
import type { ModalHeaderPropsInterface } from './types/modal-header.types';

/**
 * @since 10.0.0
 */
export const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderPropsInterface>(
  (props: ModalHeaderPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;
    const { modal } = useContext(ModalRootContext);
    const id = useId();
    const styles = css(modalHeaderStyles, cssProp);

    useIsomorphicLayoutEffect(() => {
      modal.setLabelId(id);
      return () => modal.setLabelId(undefined);
    }, [id, modal.setLabelId]);

    return (
      <div
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Modal.Header"
        id={id}
        className={cx('c11n', styles, className)}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

ModalHeader.displayName = 'Modal.Header';
